// RADAR INSTITUCIONAL · R02 — capa de PRESENTACIÓN del bloque `radar-institucional-r02-1`.
// Sólo reorganiza lo que el servidor ya autorizó: no infiere identidad, ni ubicación, ni cobertura,
// ni convierte telemetría en casos. Las unidades (casos / personas / habitaciones / camas / equipos)
// se conservan separadas y se enuncian en la UI.
import { verboEvento } from "@/lib/consolaV3/meta";
import { CAPACIDAD_LABEL } from "@/lib/alarmMeta";

export const CONTRATO_R02 = "radar-institucional-r02-1";

const rank = (p) => (p === "maxima" ? 0 : p === "alta" ? 1 : p === "media" ? 2 : p === "baja" ? 3 : 4);
export const rankCaso = rank;

// Orden natural de habitaciones: «101» antes que «1011», «Ala Norte 2» antes que «Ala Norte 10».
export function compararNatural(a = "", b = "") {
  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}

export const CAP_LABEL = (c) => CAPACIDAD_LABEL[c] || c;

/**
 * Etiqueta del contador de casos de una habitación. Una carga PARCIAL nunca se presenta como total
 * definitivo: mientras queden páginas por leer se dice «cargados», y si la lectura no se pudo
 * confirmar se conserva la presentación no confirmable (sin inventar un cero ni un total).
 */
export function etiquetaCasosHabitacion(n, { hayMas = false, noConfirmado = false } = {}) {
  if (noConfirmado) return "casos no confirmados";
  if (!n) return null;
  if (hayMas) return `${n} ${n === 1 ? "caso cargado" : "casos cargados"}`;
  return `${n} ${n === 1 ? "caso" : "casos"}`;
}

/**
 * LÍNEA SUPERIOR DE ESTADO de la tarjeta de habitación (v2.19.6).
 * Depende EXCLUSIVAMENTE de los casos ABIERTOS ya atribuidos a esta habitación por su ubicación
 * HISTÓRICA (casos de cama + casos de ámbito de habitación, los mismos que alimentan `peorRank`):
 * nunca se traslada un caso a la cama actual de otro residente. Prevalece la prioridad más alta y
 * un caso TOMADO sigue abierto, así que conserva su color.
 * NO describe cobertura de sensores ni confirma bienestar: sólo comunica los casos CONOCIDOS del
 * Radar. Por eso el azul (ausencia de casos) exige lectura confirmada y paginación agotada: una
 * carga parcial jamás se presenta como «sin casos».
 */
export const LINEA_ESTADO = {
  alta: { estado: "alta", clase: "gv2-room-line-alta",
    titulo: "Caso de prioridad ALTA abierto en esta habitación. La línea refleja casos conocidos del Radar; no confirma la cobertura de los sensores ni el bienestar de nadie." },
  media: { estado: "media", clase: "gv2-room-line-media",
    titulo: "Caso de prioridad MEDIA abierto en esta habitación. La línea refleja casos conocidos del Radar; no confirma la cobertura de los sensores ni el bienestar de nadie." },
  otros: { estado: "otros", clase: "gv2-room-line-neutra",
    titulo: "Hay casos abiertos en esta habitación sin prioridad ALTA ni MEDIA confirmada. No se afirma ausencia de casos." },
  parcial: { estado: "parcial", clase: "gv2-room-line-neutra",
    titulo: "Lectura parcial: quedan casos del centro por cargar. No se puede afirmar que esta habitación no tenga casos abiertos." },
  no_confirmado: { estado: "no_confirmado", clase: "gv2-room-line-neutra",
    titulo: "Lectura no confirmada o ubicación no verificable: no se puede determinar la ausencia de casos abiertos." },
  sin_casos: { estado: "sin_casos", clase: "gv2-room-line-ok",
    titulo: "Sin casos abiertos conocidos en esta habitación con la lectura del centro confirmada. No confirma la cobertura de los sensores ni el bienestar de nadie." },
};

export function lineaEstadoHabitacion(h, { hayMas = false, noConfirmado = false } = {}) {
  const peor = Number.isFinite(h?.peorRank) ? h.peorRank : 9;
  // maxima(0) y alta(1) comparten la presentación ALTA; media(2) es ámbar.
  if (peor <= 1) return LINEA_ESTADO.alta;
  if (peor === 2) return LINEA_ESTADO.media;
  // Casos abiertos conocidos sin prioridad ALTA/MEDIA: no es «sin casos», así que tampoco es azul.
  if ((h?.totalCasos ?? 0) > 0) return LINEA_ESTADO.otros;
  if (noConfirmado) return LINEA_ESTADO.no_confirmado;
  if (hayMas) return LINEA_ESTADO.parcial;
  // Agrupación de residentes sin cama verificable: su ubicación no se puede confirmar.
  if (h?.sinUbicacion) return LINEA_ESTADO.no_confirmado;
  return LINEA_ESTADO.sin_casos;
}

// Etiquetas de cobertura POR CAPACIDAD. «Cero equipos» describe lo conocido de ESA capacidad: no
// afirma que la persona no tenga ningún sensor ni que esté desprotegida.
export const COBERTURA_TEXTO = {
  protegida: "Cobertura confirmada",
  perdida: "Cobertura interrumpida",
  no_confirmable: "Cobertura no confirmable",
  sin_cobertura: "Sin equipo registrado para esta capacidad",
};
export const coberturaTexto = (estado) => COBERTURA_TEXTO[estado] || "Cobertura no confirmada";
const COB_RANGO = { perdida: 3, no_confirmable: 2, sin_cobertura: 1, protegida: 0 };
// P4-B · el verde es EXCLUSIVO de `protegida`. Un estado desconocido se presenta como no
// confirmado (neutro), nunca como correcto. Esto no altera C2: sólo la presentación.
export const coberturaTono = (estado) =>
  estado === "protegida" ? "ok"
    : estado === "perdida" ? "bad"
      : estado === "no_confirmable" ? "warn"
        : estado === "sin_cobertura" ? "muted"
          : "warn";

const ORIGEN_TEXTO = {
  explicito: "equipo asignado a la persona",
  cama: "equipo con ámbito de cama",
  zona: "equipo con ámbito de habitación",
};
export const origenTexto = (o) => ORIGEN_TEXTO[o] || "origen no determinado";

export function coberturaResumenPersona(lista) {
  if (!lista || lista.length === 0) return null;
  const peor = [...lista].sort((a, b) => (COB_RANGO[b.estado] || 0) - (COB_RANGO[a.estado] || 0))[0];
  return {
    estado: peor.estado,
    capacidad: peor.capacidad,
    tono: coberturaTono(peor.estado),
    texto: `${CAP_LABEL(peor.capacidad)}: ${coberturaTexto(peor.estado)}`,
    detalle: lista.map((c) => ({
      capacidad: CAP_LABEL(c.capacidad),
      estado: c.estado,
      texto: coberturaTexto(c.estado),
      origen: c.origen ? origenTexto(c.origen) : "sin origen determinado",
      equipos: c.dispositivos_total,
      caidos: c.dispositivos_caidos,
      tono: coberturaTono(c.estado),
    })),
  };
}

// Dónde ocurrió, en texto, SIN rellenar lo que el caso no capturó.
export function ubicacionTexto(u) {
  if (!u) return "Ubicación no confirmada";
  if (u.nivel === "cama") return `${u.habitacion} · ${u.cama_etiqueta || "cama"}`;
  if (u.nivel === "habitacion") return u.habitacion || "Habitación";
  if (u.nivel === "persona") return "Ubicación no confirmada · referencia no capturada en el caso";
  return "Ubicación no confirmada · nivel centro";
}

// P4-D · ESTADO REAL del caso. `recibida` es Pendiente; los estados canónicos de toma son «En
// atención» (con operador cuando conste); cualquier otro estado abierto se enuncia TAL CUAL, sin
// clasificarlo automáticamente como pendiente.
const ESTADO_TEXTO = {
  recibida: "Pendiente",
  tomada: "En atención",
  en_verificacion: "En verificación",
  no_verificable: "No verificable",
  escalada: "Escalada",
  en_espera: "En espera",
};
export function estadoLabel(c) {
  const base = ESTADO_TEXTO[c?.estado] || (c?.estado ? String(c.estado).replace(/_/g, " ") : "Estado no informado");
  return c?.en_atencion && c?.operador ? `${base} · ${c.operador}` : base;
}
export const esPendiente = (c) => c?.estado === "recibida";

/**
 * Proyección de presentación. `snap` es la respuesta COMPLETA de /api/radar/institucional.
 * El bloque legado sólo se usa para CONTEXTO por residente (actividad reciente observada).
 */
export function proyectarR02(snap, casosAcumulados = null) {
  const r02 = snap?.r02 || null;
  const lectura = r02?.lectura || null;
  const residentes = snap?.residentes || [];
  const porPersona = {};
  residentes.forEach((t) => { porPersona[t.id] = t; });

  const coberturaPorPersona = {};
  (r02?.cobertura_contexto || []).forEach((c) => {
    (coberturaPorPersona[c.persona_id] = coberturaPorPersona[c.persona_id] || []).push(c);
  });

  const casos = (casosAcumulados || r02?.casos || []).map((c) => ({
    ...c,
    verbo: verboEvento(c.tipo),
    donde: ubicacionTexto(c.ubicacion),
    estadoLabel: estadoLabel(c),
    pendiente: esPendiente(c),
  }));

  // P3 · UBICACIÓN HISTÓRICA. Un caso sólo se destaca donde FUE CAPTURADO y verificado: por
  // `cama_id` en la cama, por `zona_id` en la habitación. Jamás en la cama actual del residente
  // (pudo trasladarse después del evento). Los casos sin ubicación verificable no se colocan en
  // ninguna cama ni habitación: viven en el carril de atención, que los conserva todos.
  const casosPorCama = {};
  const casosPorZona = {};
  const casosPorPersona = {};
  casos.forEach((c) => {
    if (c.persona_id) (casosPorPersona[c.persona_id] = casosPorPersona[c.persona_id] || []).push(c);
    const u = c.ubicacion || {};
    if (u.nivel === "cama" && u.cama_id) {
      (casosPorCama[u.cama_id] = casosPorCama[u.cama_id] || []).push(c);
    } else if (u.nivel === "habitacion" && u.zona_id) {
      (casosPorZona[u.zona_id] = casosPorZona[u.zona_id] || []).push(c);
    }
  });

  const habitaciones = (r02?.habitaciones || []).map((h) => {
    const camas = (h.camas || []).map((cama) => {
      const pid = cama.persona_id || null;
      const t = pid ? porPersona[pid] : null;
      const propios = cama.cama_id ? casosPorCama[cama.cama_id] || [] : [];
      const principal = [...propios].sort((a, b) => rank(a.prioridad) - rank(b.prioridad)
        || String(a.desde || "").localeCompare(String(b.desde || "")))[0] || null;
      return {
        cama_id: cama.cama_id,
        etiqueta: cama.etiqueta,
        persona_id: pid,
        persona_nombre: cama.persona_nombre || null,
        sinResidente: !pid,
        // Casos capturados EN ESTA CAMA (no «los casos de quien hoy la ocupa»).
        casos: propios,
        caso: principal,
        // Casos de esta persona capturados en OTRA ubicación: se señalan sin moverlos aquí.
        casosEnOtraUbicacion: pid
          ? (casosPorPersona[pid] || []).filter((c) => !propios.includes(c)).length
          : 0,
        cobertura: pid ? coberturaResumenPersona(coberturaPorPersona[pid]) : null,
        actividad: t?.actividad_reciente ? { hora: t.actividad_reciente_hora, tipo: (t.hechos || [])[0]?.tipo } : null,
        ultimoHecho: (t?.hechos || [])[0] || null,
      };
    });
    const casosHabitacion = h.zona_id ? casosPorZona[h.zona_id] || [] : [];
    // P4-C · se cuentan CASOS reales: una cama con dos casos aporta dos.
    const casosEnCamas = camas.reduce((n, c) => n + c.casos.length, 0);
    return {
      zona_id: h.zona_id,
      nombre: h.nombre,
      tipo: h.tipo,
      sinUbicacion: !h.zona_id,
      camas,
      casosHabitacion,
      totalCasos: casosEnCamas + casosHabitacion.length,
      pendientes: camas.reduce((n, c) => n + c.casos.filter((x) => x.pendiente).length, 0)
        + casosHabitacion.filter((c) => c.pendiente).length,
      peorRank: Math.min(
        9,
        ...camas.flatMap((c) => c.casos.map((x) => rank(x.prioridad))),
        ...casosHabitacion.map((c) => rank(c.prioridad)),
      ),
      buscable: [h.nombre, ...camas.map((c) => c.persona_nombre || ""), ...camas.map((c) => c.etiqueta || "")]
        .filter(Boolean).join(" ").toLowerCase(),
    };
  });

  // P4-A · cuando el ensamblado r02 no se pudo confirmar, los totales viajan como NULL y la UI
  // muestra «—» con el aviso de lectura no confirmada. Un fallo NO es «cero habitaciones, cero
  // camas ni cero casos».
  const confirmado = r02?.contrato === CONTRATO_R02 && lectura?.completo !== false;
  const noConfirmado = !!lectura && lectura.completo === false && !lectura.casos_total_consultados
    && (r02?.habitaciones || []).length === 0;

  return {
    disponible: r02?.contrato === CONTRATO_R02,
    confirmado,
    noConfirmado,
    casos,
    resumenCasos: r02?.resumen_casos || null,
    habitaciones,
    lectura,
    totalCamas: noConfirmado ? null
      : habitaciones.reduce((n, h) => n + h.camas.filter((c) => c.cama_id).length, 0),
    totalHabitaciones: noConfirmado ? null : habitaciones.filter((h) => h.zona_id).length,
  };
}

export function ordenarHabitaciones(habitaciones, orden) {
  const base = [...habitaciones];
  // El orden ESPACIAL (por número de habitación) es el estable e inicial. «Estado» es una
  // reordenación VISUAL: no cambia habitación, cama, residente, asignaciones ni responsabilidades.
  base.sort((a, b) => {
    if (a.sinUbicacion !== b.sinUbicacion) return a.sinUbicacion ? 1 : -1;
    if (orden === "estado" && a.peorRank !== b.peorRank) return a.peorRank - b.peorRank;
    return compararNatural(a.nombre, b.nombre);
  });
  return base;
}
